import { useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import {
  SellerOrder,
  SellerOrderConnection,
  SellerOrderStatus,
} from '@shopify-multiseller/graphql/partner/types';
import { ORDERS_QUERY, ORDER_STATUS_COUNT_QUERY } from './queries';
import { Inbox, CalendarDays, DollarSign } from 'lucide-react';
import { AdminUI } from '../../ui/AdminUI';
import { Button } from '../../components/primitives';
import { DataList, type DataListColumn } from '../../components/DataList';
import { CommandHeader } from '../../components/CommandHeader';
import { FilterBuilder, type AppliedFilters, type FilterField } from '../../components/FilterBuilder';
import { presetDateIso } from '../../components/FilterBuilder/presetDate';
import { SaveBarBlocker } from '../../context/SaveBarBlocker';
import { SellerOrderStatusBadge } from '../../components/StatusBadge/SellerOrderStatusBadge';
import { formatCents, formatRelativeTime } from '../../helpers/formatters';
import { useShopCurrency } from '../../hooks';
import { useListView } from '../../hooks/useListView';

type SellerOrdersData = { sellerOrderConnection: SellerOrderConnection };
type CountData = { sellerOrderConnection: { count: number } };

const selectOrderConnection = (data?: SellerOrdersData) => data?.sellerOrderConnection;

const buildOrderVariables = ({
  first,
  filter,
  after,
  before,
  filters,
}: {
  first: number;
  filter: string;
  after?: string;
  before?: string;
  filters: AppliedFilters;
}) => {
  const status = filters.status?.type === 'enum' ? filters.status.values : [];
  const placed = filters.placed?.type === 'date' ? filters.placed : undefined;
  const total = filters.total?.type === 'number' ? filters.total : undefined;

  return {
    first,
    filter,
    after,
    before,
    sellerOrderStatus: status.length > 0 ? (status as SellerOrderStatus[]) : undefined,
    orderedAfter: placed?.presetDays !== undefined ? presetDateIso(placed.presetDays) : placed?.from,
    orderedBefore: placed?.presetDays !== undefined ? undefined : placed?.to,
    // Zero is a real bound, so these check for null rather than falsiness.
    totalMin: total?.min ?? undefined,
    totalMax: total?.max ?? undefined,
  };
};

const FILTER_FIELDS: FilterField[] = [
  {
    key: 'status',
    label: 'Status',
    type: 'enum',
    multiple: true,
    icon: Inbox,
    options: [
      { value: SellerOrderStatus.Pending, label: 'Pending' },
      { value: SellerOrderStatus.PendingFulfillment, label: 'To fulfil' },
      { value: SellerOrderStatus.Fulfilled, label: 'Fulfilled' },
      { value: SellerOrderStatus.WaitingThirdParty, label: 'Waiting third party' },
      { value: SellerOrderStatus.Rejected, label: 'Rejected' },
      { value: SellerOrderStatus.Cancelled, label: 'Cancelled' },
      { value: SellerOrderStatus.AcceptTimeout, label: 'Accept timeout' },
      { value: SellerOrderStatus.FulfillmentTimeout, label: 'Fulfilment timeout' },
    ],
  },
  {
    key: 'placed',
    label: 'Placed',
    type: 'date',
    icon: CalendarDays,
    presets: [
      { label: 'Last 7 days', days: 7 },
      { label: 'Last 30 days', days: 30 },
      { label: 'Last 90 days', days: 90 },
    ],
  },
  { key: 'total', label: 'Total', type: 'number', prefix: '$', icon: DollarSign },
];

/** "Juniper Throw ×1", or a count once naming them stops being useful. */
const contents = (order: SellerOrder): string => {
  const items = order.items ?? [];
  if (items.length === 0) return '—';
  if (items.length > 2) return `${items.length} products`;
  return items.map((item) => `${item.title} ×${item.quantity}`).join(', ');
};

const columns = (currencyCode: string): DataListColumn<SellerOrder>[] => [
  {
    header: 'Order',
    className: 'w-[38%]',
    cell: (order) => (
      <span className="block min-w-0">
        <span className="block truncate font-semibold text-foreground">#{order.idAlias}</span>
        <span className="block truncate text-xs text-muted-foreground">{contents(order)}</span>
      </span>
    ),
  },
  {
    header: 'Status',
    cell: (order) => (order.status ? <SellerOrderStatusBadge status={order.status} dot /> : null),
  },
  {
    header: 'Placed',
    align: 'right',
    cell: (order) => (
      <span className="whitespace-nowrap text-muted-foreground">
        {formatRelativeTime(order.orderDate)}
      </span>
    ),
  },
  {
    header: 'Total',
    align: 'right',
    cell: (order) => (
      <span className="tabular-nums text-foreground">
        {formatCents({ amount: order.total, currencyCode })}
      </span>
    ),
  },
];

export const SellerOrders: React.FC = () => {
  const navigate = useNavigate();
  const currencyCode = useShopCurrency();
  const list = useListView<SellerOrdersData, SellerOrder, AppliedFilters>({
    query: ORDERS_QUERY,
    selectConnection: selectOrderConnection,
    buildVariables: buildOrderVariables,
    initialFilters: {},
    pageSize: 5,
  });

  const hasFilters = Object.keys(list.filters).length > 0;

  // Counts describe the whole book of orders, not the filtered view — they are
  // what tells a seller whether there is a queue to clear.
  const { data: pending } = useQuery<CountData>(ORDER_STATUS_COUNT_QUERY, {
    variables: { sellerOrderStatus: [SellerOrderStatus.Pending] },
  });
  const { data: toFulfil } = useQuery<CountData>(ORDER_STATUS_COUNT_QUERY, {
    variables: { sellerOrderStatus: [SellerOrderStatus.PendingFulfillment] },
  });
  const awaiting = pending?.sellerOrderConnection.count ?? 0;

  return (
    <AdminUI>
      <SaveBarBlocker>
        <div className="min-h-screen">
          <CommandHeader
            title="Orders"
            counts={[
              {
                label: 'Awaiting acceptance',
                value: awaiting,
                testId: 'count-pending',
              },
              {
                label: 'To fulfil',
                value: toFulfil?.sellerOrderConnection.count ?? 0,
                testId: 'count-to-fulfil',
              },
            ]}
            actions={
              // Only offered when there is actually a queue — the split view is
              // for clearing a backlog, not the everyday way in to orders.
              awaiting > 0 ? (
                <Button
                  data-testid="review-queue-button"
                  onClick={() => navigate('/orders/triage')}
                >
                  Review queue
                </Button>
              ) : undefined
            }
          />

          <div className="mx-auto max-w-7xl px-6 py-5">
            <DataList
              variant="open"
              testId="orders-list"
              list={list}
              columns={columns(currencyCode)}
              getRowId={(order) => order.id}
              onRowClick={(order) => navigate(`/orders/${order.id}`)}
              searchPlaceholder="Search # or customer…"
              filterSlot={
                <FilterBuilder
                  fields={FILTER_FIELDS}
                  filters={list.filters}
                  onChange={list.setFilters}
                />
              }
              sortLabel="Sorted by newest"
              matchCount={hasFilters ? list.count : undefined}
              emptyNarrowed={hasFilters}
              emptyIcon={Inbox}
              emptyTitle={hasFilters ? 'Nothing matches these filters' : 'No orders yet'}
              emptyDescription={
                hasFilters
                  ? undefined
                  : 'Orders appear the moment a customer buys one of your live products.'
              }
              emptyAction={
                hasFilters
                  ? { label: 'Clear filters', onAction: () => list.setFilters({}) }
                  : undefined
              }
            />
          </div>
        </div>
      </SaveBarBlocker>
    </AdminUI>
  );
};
